import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Briefcase, ArrowRight, Sparkles } from "lucide-react";
import api from "@/lib/api";
import Seo from "@/components/seo/Seo";
import FaqSection from "@/components/common/FaqSection";
import { CAREERS_FAQS } from "@/data/faqs";

export default function Careers() {
  const [jobs, setJobs] = useState([]);
  const [dept, setDept] = useState("all");
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/jobs").then((r) => setJobs(r.data)).catch(() => {});
  }, []);

  const departments = ["all", ...Array.from(new Set(jobs.map((j) => j.department)))];
  const filtered = jobs.filter((j) => {
    const matchesDept = dept === "all" || j.department === dept;
    const matchesQ = !q ||
      j.title.toLowerCase().includes(q.toLowerCase()) ||
      j.location.toLowerCase().includes(q.toLowerCase()) ||
      j.summary.toLowerCase().includes(q.toLowerCase());
    return matchesDept && matchesQ;
  });

  return (
    <div className="max-w-6xl mx-auto px-6 py-16 md:py-20" data-testid="careers-page">
      <Seo
        title="Careers"
        description="Join CloudLab Technologies — engineering, design, AI and support roles across the UK and Pakistan. Remote-friendly, senior-only, respectful hours."
        keywords="CloudLab careers, software engineer jobs Pakistan, AI engineer jobs, remote engineering jobs, jobs Islamabad, jobs London"
      />
      <div className="inline-flex items-center gap-2 mb-6">
        <Sparkles className="w-3.5 h-3.5 text-primary" />
        <span className="text-xs font-mono uppercase tracking-[0.25em] text-primary">Join CloudLab</span>
      </div>
      <h1 className="font-heading font-bold text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05]" data-testid="careers-title">
        Build the AI that runs the enterprise.
      </h1>
      <p className="mt-5 text-lg text-muted-foreground max-w-2xl">
        We are a distributed team of engineers, designers and go-to-market operators shipping production AI for
        ambitious companies across the UK, EU, US and Middle East. Remote-first, top-of-market, no legacy.
      </p>

      {/* Search + filter */}
      <div className="mt-10 flex flex-col md:flex-row gap-3">
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search roles, locations, keywords…"
          className="flex-1 bg-transparent border border-border rounded-full px-5 py-3 text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30"
          data-testid="careers-search"
        />
        <div className="flex gap-2 overflow-x-auto no-scrollbar">
          {departments.map((d) => (
            <button
              key={d}
              onClick={() => setDept(d)}
              data-testid={`careers-dept-${d.toLowerCase().replace(/\W+/g, '-')}`}
              className={`shrink-0 px-4 py-2 rounded-full text-xs font-medium border transition-colors ${
                dept === d
                  ? "bg-primary text-primary-foreground border-primary"
                  : "border-border hover:border-primary/40"
              }`}
            >
              {d === "all" ? "All departments" : d}
            </button>
          ))}
        </div>
      </div>

      {/* Job list */}
      <div className="mt-10 space-y-3" data-testid="careers-list">
        {filtered.length === 0 && (
          <div className="card-clean p-10 text-center text-muted-foreground">
            No open roles matching your filter. Send us your CV anyway — we hire when great people show up.
          </div>
        )}
        {filtered.map((j) => (
          <Link
            key={j.id}
            to={`/careers/${j.slug}`}
            data-testid={`job-card-${j.slug}`}
            className="group card-clean p-6 flex flex-col md:flex-row md:items-center gap-4 md:gap-6 hover:border-primary/40 transition-colors"
          >
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1"><Briefcase className="w-3 h-3" /> {j.department}</span>
                <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {j.location}</span>
                <span className="px-2 py-0.5 rounded-full bg-muted text-[10px] font-mono uppercase tracking-[0.15em]">{j.type}</span>
                {j.remote && <span className="px-2 py-0.5 rounded-full bg-primary/10 text-primary text-[10px] font-mono uppercase tracking-[0.15em]">Remote</span>}
              </div>
              <h2 className="mt-3 font-heading font-bold text-xl md:text-2xl leading-tight">{j.title}</h2>
              <p className="mt-2 text-sm text-muted-foreground line-clamp-2">{j.summary}</p>
            </div>
            <div className="shrink-0 w-11 h-11 rounded-full border border-border flex items-center justify-center group-hover:bg-primary group-hover:text-primary-foreground group-hover:border-primary transition-colors">
              <ArrowRight className="w-4 h-4" />
            </div>
          </Link>
        ))}
      </div>

      <FaqSection kicker="Careers · FAQ" title="What you should know before you apply." items={CAREERS_FAQS} testidRoot="careers-faq" />
    </div>
  );
}
